import { useCallback, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '../../lib/utils'
import { ContextUploadIcon } from './ContextUploadIcon'
import { Text } from './Text'
import type { FileDropzoneProps } from '../../types/file-dropzone'

/**
 * Drag-and-drop + click-to-browse file upload zone.
 *
 * Visual drag-over state is managed internally so consumers
 * only need to handle the `onFilesSelected` callback.
 */
const CONTEXT_MATERIALS_HINT =
  'Interview transcripts, decks, briefs – PDF, DOCX, PPTX or text'

export function FileDropzone({
  accept,
  disabled = false,
  multiple = false,
  className,
  variant = 'default',
  label = 'Drag & drop files here or click to browse',
  dragLabel = 'Drop files here',
  hint,
  onFilesSelected,
}: FileDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const zoneRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isContextMaterials = variant === 'context-materials'
  const effectiveHint = isContextMaterials ? CONTEXT_MATERIALS_HINT : hint

  /* ---- Drag handlers ---- */

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!disabled) setIsDragOver(true)
    },
    [disabled],
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      // Only clear when truly leaving the zone (not a child boundary)
      if (zoneRef.current && !zoneRef.current.contains(e.relatedTarget as Node)) {
        setIsDragOver(false)
      }
    },
    [],
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      if (disabled) return
      if (e.dataTransfer.files.length > 0) {
        onFilesSelected(e.dataTransfer.files)
      }
    },
    [disabled, onFilesSelected],
  )

  /* ---- Click-to-browse ---- */

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click()
  }, [disabled])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesSelected(e.target.files)
      }
      // Reset so the same file can be re-selected
      if (inputRef.current) inputRef.current.value = ''
    },
    [onFilesSelected],
  )

  return (
    <div
      ref={zoneRef}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick()
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        'text-center transition-colors cursor-pointer',
        isContextMaterials
          ? 'rounded-xl border-[1.5px] border-dashed border-muted-foreground/25 dark:border-[#3e6161] px-6 py-10'
          : 'rounded-lg border-2 border-dashed p-6',
        !isContextMaterials &&
          (isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'),
        isContextMaterials &&
          (isDragOver ? 'border-cta bg-cta/10' : ''),
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
        disabled={disabled}
      />
      {isContextMaterials ? (
        <ContextUploadIcon className="h-[60px] w-[120px] max-w-full" />
      ) : (
        <Upload
          className={cn(
            'mx-auto mb-2 h-6 w-6',
            isDragOver ? 'text-primary' : 'text-muted-foreground',
          )}
        />
      )}
      {isContextMaterials ? (
        <p className="text-base leading-[1.6] text-foreground dark:text-white">
          {isDragOver ? (
            dragLabel
          ) : (
            <>
              Drop your context files here or{' '}
              <span className="text-primary dark:text-[#d1ffeb]">click to browse</span>
            </>
          )}
        </p>
      ) : (
        <Text size="sm" color="muted">
          {isDragOver ? dragLabel : label}
        </Text>
      )}
      {effectiveHint && (
        <p
          className={cn(
            'mt-1',
            isContextMaterials
              ? 'text-base leading-[1.6] text-muted-foreground dark:text-[#BDBDBD]'
              : 'text-xs text-muted-foreground/60',
          )}
        >
          {effectiveHint}
        </p>
      )}
    </div>
  )
}
